/**
 * N-06 «Внутренний баннер о новом событии».
 *
 * ПС §12, §17: пока вкладка видима, системное уведомление не показывается
 * (CC §9.6) — вместо него короткий баннер о событии ДРУГОГО диалога.
 * Событие открытого диалога баннером не дублируется: оно и так на экране.
 *
 * §8.6 п. 2: звук без действия пользователя и мигание ЗАПРЕЩЕНЫ.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'

import { faceOf } from '@entities/conversation/display'
import type { Match, Message, PublicPhoto } from '@shared/api/types'
import { api, unwrap } from '@shared/api/client'
import { conversationsRepo } from '@shared/db'
import { Avatar, Icon, cx } from '@shared/ui'

import { subscribeInAppEvents } from '../../model/in-app-events'
import { useAppStore } from '../../model/app-store'
import styles from './in-app.module.css'

/** Баннер живёт ровно столько, чтобы его успели прочитать и нажать. */
const VISIBLE_MS = 6000

interface BannerData {
  key: string
  title: string
  text: string
  photo: PublicPhoto | null
  route: { to: string; params?: Record<string, string> }
  tone: 'match' | 'message'
}

export function InAppBanner(): React.ReactElement | null {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [banner, setBanner] = useState<BannerData | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Путь нужен «на момент события», а не «на момент подписки».
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  useEffect(() => {
    const show = (data: BannerData): void => {
      setBanner(data)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setBanner(null), VISIBLE_MS)
    }

    const unsubscribe = subscribeInAppEvents((event) => {
      if (event.kind === 'match') {
        show(describeMatch(event.match))
        return
      }

      void describeMessage(event.message, pathnameRef.current).then((data) => {
        if (data) show(data)
      })
    })

    return () => {
      unsubscribe()
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  if (!banner) return null

  return (
    // Живая область: доступна и поверх модального слоя (см. modal-stack.ts)
    <div className={styles['layer']} data-live-region role="status">
      <div className={cx(styles['banner'], styles[banner.tone])}>
        {/* Открыть — одна кнопка, закрыть — другая: вложенных интерактивных элементов нет */}
        <button
          type="button"
          className={styles['open']}
          onClick={() => {
            setBanner(null)
            void navigate(banner.route as never)
          }}
        >
          <span className={styles['avatar']}>
            {banner.tone === 'match' && !banner.photo ? (
              <Icon name="heart-filled" size={20} />
            ) : (
              <Avatar name={banner.title} photo={banner.photo} size="sm" />
            )}
          </span>

          <span className={styles['body']}>
            <span className={styles['title']}>{banner.title}</span>
            <span className={styles['text']}>{banner.text}</span>
          </span>
        </button>

        <button type="button" className={styles['close']} aria-label="Скрыть" onClick={() => setBanner(null)}>
          <Icon name="close" size={16} />
        </button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* Описание события                                                       */
/* ===================================================================== */

function describeMatch(match: Match): BannerData {
  const name = match.counterpart.display_name ?? 'Новое совпадение'

  return {
    key: `match:${match.id}`,
    title: name,
    // Первое фото карточки — то же, что видно в списке совпадений.
    photo: match.counterpart.photos[0] ?? null,
    text: 'Вы оба за движ',
    route: { to: '/contacts' },
    tone: 'match',
  }
}

/**
 * Текст берётся из уже применённого события — оно пришло по защищённому
 * каналу под текущей сессией, поэтому дополнительного запроса не требуется.
 * Настройка `notif_preview` относится к СИСТЕМНОМУ уведомлению (CC §9.4) и
 * на интерфейс внутри приложения не распространяется.
 */
async function describeMessage(message: Message, pathname: string): Promise<BannerData | null> {
  // Диалог открыт — событие уже видно в ленте, баннер был бы дублем.
  if (
    message.topic
      ? pathname.startsWith(`/chats/${message.conversation_id}/topics/${message.topic.id}`)
      : pathname.startsWith(`/chats/${message.conversation_id}`)
  )
    return null

  const conversation = await conversationsRepo.get(message.conversation_id)
  if (!conversation) return null
  if (conversation.my.muted_until && Date.parse(conversation.my.muted_until) > Date.now()) return null
  if (conversation.my.notification_mode === 'none') return null
  const ownId = useAppStore.getState().me?.user.id
  const attention = Boolean(
    ownId &&
      (message.reply_to?.sender_id === ownId ||
        message.entities?.some((entity) => entity.type === 'mention' && entity.user_id === ownId)),
  )
  if (conversation.my.notification_mode === 'mentions' && !attention) return null
  if (message.topic && conversation.channel?.id) {
    try {
      const topic = unwrap(
        await api.GET('/channels/{channelId}/topics/{topicId}', {
          params: { path: { channelId: conversation.channel.id, topicId: message.topic.id } },
        }),
      )
      if (topic.notification_mode === 'none' || (topic.notification_mode === 'mentions' && !attention))
        return null
    } catch {
      // Without the current topic setting, a banner could disclose muted content.
      return null
    }
  }

  const face = faceOf(conversation)
  return {
    key: `message:${message.id}`,
    title: message.topic
      ? `${face.name ?? 'Группа'} · #${message.topic.title}`
      : (face.name ?? 'Новое сообщение'),
    text: previewOf(message),
    photo: face.photo,
    route: message.topic
      ? {
          to: '/chats/$conversationId/topics/$topicId',
          params: { conversationId: message.conversation_id, topicId: message.topic.id },
        }
      : { to: '/chats/$conversationId', params: { conversationId: message.conversation_id } },
    tone: 'message',
  }
}

function previewOf(message: Message): string {
  switch (message.type) {
    case 'image':
      return 'Фотография'
    case 'video':
      return 'Видео'
    case 'voice':
      return 'Голосовое сообщение'
    default:
      return message.text?.trim() || 'Новое сообщение'
  }
}
